import React from 'react';
import SiteLayout from '../components/SiteLayout';
import { Check, Shield, FileText, Globe, Lock, Leaf, Zap, Heart, Car, RefreshCw, UserCheck, HardHat, UtensilsCrossed, Plane, Microscope, Loader2, Server, Building2, Scale, Briefcase, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { useCMSContent } from '../hooks/useCMSContent';

// Icon mapping for dynamic icon rendering from CMS
const iconMap = {
  Shield, FileText, Globe, Lock, Leaf, Zap, Heart, Car, RefreshCw, UserCheck, HardHat, UtensilsCrossed, Plane, Microscope, Server, Building2, Scale, Briefcase
};

const StandardsPage = () => {
  const { content: cmsContent, loading } = useCMSContent('standards-page');

  if (loading) {
    return (
      <SiteLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SiteLayout>
    );
  }

  // Use CMS content only - no fallbacks
  const hero = cmsContent?.hero;
  const categories = cmsContent?.categories;
  const features = cmsContent?.features;
  const featuresTitle = cmsContent?.featuresTitle;
  const cta = cmsContent?.cta;

  // Show error if required CMS content is not configured
  if (!hero) {
    return (
      <SiteLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">CMS Content Required</h2>
            <p className="text-muted-foreground mb-4">
              Standards page content is not configured. Please add content to the CMS document: <code className="bg-muted px-1 rounded">cms_content/standards-page</code>
            </p>
            <p className="text-sm text-muted-foreground">
              Required fields: hero
            </p>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      {/* Hero */}
      {hero && (
        <section className="py-20 bg-muted/30">
          <div className="container-wide text-center">
            {hero.title && (
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {hero.title}
              </h1>
            )}
            {hero.subtitle && (
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {hero.subtitle}
              </p>
            )}
            {hero.heroImage && (
              <div className="mt-12 relative mx-auto max-w-5xl rounded-xl overflow-hidden shadow-2xl border border-border/50">
                <img
                  src={hero.heroImage}
                  alt={hero.heroImageAlt || ''}
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Standards by Category */}
      {categories && categories.map((category, catIndex) => (
        <section key={catIndex} className="py-16 border-b border-border last:border-b-0">
          <div className="container-wide">
            {category.name && <h2 className="text-2xl font-bold mb-8 text-center">{category.name}</h2>}
            {category.standards && category.standards.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.standards.map((std, index) => {
                  const Icon = iconMap[std.icon] || Shield;
                  return (
                    <div key={index} className="flex gap-6 p-6 bg-card border border-border rounded-xl hover:shadow-lg transition-shadow">
                      <div className="shrink-0">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                      </div>
                      <div>
                        {std.code && (
                          <div className="inline-block px-3 py-1 bg-muted rounded-full text-xs font-medium mb-2">
                            {std.code}
                          </div>
                        )}
                        {std.title && <h3 className="text-xl font-bold mb-2">{std.title}</h3>}
                        {std.description && (
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {std.description}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      ))}

      {/* Features */}
      {features && features.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container-wide">
            {featuresTitle && <h2 className="text-2xl font-bold mb-8 text-center">{featuresTitle}</h2>}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-6 text-center">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-5 h-5 text-primary" />
                  </div>
                  {feature.title && <h3 className="font-semibold mb-2">{feature.title}</h3>}
                  {feature.description && <p className="text-sm text-muted-foreground">{feature.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {cta && (
        <section className="py-20 bg-primary text-white text-center">
          <div className="container-wide">
            {cta.title && <h2 className="text-3xl font-bold mb-6">{cta.title}</h2>}
            {cta.button && cta.href && (
              <Link to={cta.href}>
                <Button size="xl" className="bg-white text-primary hover:bg-white/90">
                  {cta.button}
                </Button>
              </Link>
            )}
          </div>
        </section>
      )}
    </SiteLayout>
  );
};

export default StandardsPage;
